import Vue from '../main/vue';
import router from '../main/vue-routes';
import {getLangcode} from '../main/utils';

const toLiters = value => Number(value) * 3.78541;

Vue.filter('formatUsage', (value, unit, type) => {
  let suffix = 'f';

  if (type === 'faucet' || type === 'showerhead') {
    suffix = 'm';
  }

  if (unit === 'liters') {
    return `${toLiters(value).toFixed(1)} Lp${suffix}`;
  }

  return `${value} gp${suffix}`;
});

Vue.filter('convert', (value, useLiters) => {
  return useLiters ? Math.round(toLiters(value)) : value;
});

Vue.filter('commafy', value => {
  const str = value.toString().split('.');

  if (str[0].length >= 3) {
    str[0] = str[0].replace(/(\d)(?=(\d{3})+$)/g, '$1,');
  }

  if (str[1] && str[1].length >= 3) {
    str[1] = str[1].replace(/(\d{3})/g, '$1 ');
  }

  return str.join('.');
});

const loadCalculator = () => {
  if (document.querySelector('.sustainability-calculator')) {
    new Vue({
      el: '.sustainability-calculator',
      comments: true,
      data() {
        const scrollContainer = window;
        return {
          scrollContainer,
          productTypes: [
            {
              id: 'water-closet-combo',
              label: 'Closet Combo',
              index: this.$algolia.indexPrefix + 'closet_combos',
              variationsIndex: this.$algolia.indexPrefix + 'closet_combo_variations',
              usageField: 'combo_flushometer_flush_volume_formatted',
              leedBaseline: 1.6
            },
            {
              id: 'faucet',
              label: 'Faucet',
              index: this.$algolia.indexPrefix + 'faucets',
              variationsIndex: this.$algolia.indexPrefix + 'faucet_variations',
              usageField: 'variation_flow_rate_formatted',
              filters: '(variation_flow_rate_formatted<=0.5)',
              leedBaseline: 0.5
            },
            {
              id: 'flushometer',
              label: 'Flushometer',
              index: this.$algolia.indexPrefix + 'flushometers',
              variationsIndex: this.$algolia.indexPrefix + 'flushometer_variations',
              usageField: 'variation_flush_volume_formatted',
              filters: '(variation_flush_volume_formatted<=1.28)',
              leedBaseline: 1.6
            },
            {
              id: 'hybrid-urinal',
              label: 'Hybrid Urinal',
              index: this.$algolia.indexPrefix + 'urinals',
              variationsIndex: this.$algolia.indexPrefix + 'urinal_variations',
              filters: '(product_flush_method:\'Hybrid\')',
              leedBaseline: 1.0
            },
            {
              id: 'showerhead',
              label: 'Showerhead',
              index: this.$algolia.indexPrefix + 'showerheads',
              variationsIndex: this.$algolia.indexPrefix + 'showerhead_variations',
              usageField: 'variation_flow_rate_formatted',
              filters: '(variation_flow_rate_formatted<=2)',
              leedBaseline: 2.5
            },
            {
              id: 'urinal-combo',
              label: 'Urinal Combo',
              index: this.$algolia.indexPrefix + 'urinal_combos',
              variationsIndex: this.$algolia.indexPrefix + 'urinal_combo_variations',
              usageField: 'combo_flushometer_flush_volume_formatted',
              leedBaseline: 1.0
            },
            {
              id: 'waterfree-urinal',
              label: 'Waterfree Urinal',
              index: this.$algolia.indexPrefix + 'urinals',
              variationsIndex: this.$algolia.indexPrefix + 'urinal_variations',
              filters: '(product_flush_method:\'Waterfree\')',
              leedBaseline: 1.0
            }
          ],
          stepLabels: ['Products', 'Details', 'Results'],
          variationOptions: [],
          productOptions: {},
          usageOptions: {},
          activeStep: 1,
          currentType: '',
          currentProduct: '',
          currentUnit: 'gallons',
          currentUsage: '',
          selectedProducts: [],
          useLiters: false
        };
      },
      computed: {
        langcode() {
          return getLangcode(this.$route.params.langcode);
        },
        validations() {
          return {
            selectedProducts: this.selectedProducts.every(product => {
              return (
                product.type &&
                product.name &&
                product.unit &&
                product.usage !== ''
              );
            }),
            productDetails: this.selectedProducts.every(product => {
              return (
                product.numberProducts &&
                product.waterSewerCosts &&
                product.daysOccupancy &&
                product.numberOccupants &&
                product.dailyFlushes
              );
            })
          };
        },
        totalWaterUsage() {
          return {
            perDay: this.selectedProducts.reduce(
              (total, product) => {
                total +=
                  product.usage *
                  product.numberProducts *
                  product.numberOccupants *
                  product.dailyFlushes;
                return total;
              },
              0
            ),
            leedPerDay: this.selectedProducts.reduce(
              (total, product) => {
                const leedBaseline = this.getProductByType(product.type).leedBaseline;
                total +=
                  leedBaseline *
                  product.numberProducts *
                  product.numberOccupants *
                  product.dailyFlushes;
                return total;
              },
              0
            ),
            perYear: this.selectedProducts.reduce(
              (total, product) => {
                total +=
                  product.usage *
                  product.numberProducts *
                  product.numberOccupants *
                  product.dailyFlushes *
                  product.daysOccupancy;
                return total;
              },
              0
            ),
            leedPerYear: this.selectedProducts.reduce(
              (total, product) => {
                const leedBaseline = this.getProductByType(product.type).leedBaseline;
                total +=
                  leedBaseline *
                  product.numberProducts *
                  product.numberOccupants *
                  product.dailyFlushes *
                  product.daysOccupancy;
                return total;
              },
              0
            )
          };
        },
        totalDollarsSaved() {
          return this.selectedProducts.reduce((total, product) => {
            const gallonsPerYear =
              product.usage *
              product.numberProducts *
              product.numberOccupants *
              product.dailyFlushes *
              product.daysOccupancy;
            total += (this.totalWaterUsage.leedPerYear - gallonsPerYear) * (product.waterSewerCosts / 1000);
            return total;
          }, 0);
        },
        savings() {
          function getLeedPoints(value) {
            if (value >= 0.5) return 6;
            if (value >= 0.45) return 5;
            if (value >= 0.4) return 4;
            if (value >= 0.35) return 3;
            if (value >= 0.3) return 2;
            if (value >= 0.25) return 1;
            return 0;
          }

          let baselinePercentageReduction = this.selectedProducts.reduce(
            (result, product) => {
              const value = 1 - product.usage / this.getProductByType(product.type).leedBaseline;
              return value <= 1
                ? (result += value)
                : (result -= value);
            },
            0
          );

          baselinePercentageReduction = Math.max(0, baselinePercentageReduction);

          return {
            gallonsSaved: Math.round(this.totalWaterUsage.leedPerYear - this.totalWaterUsage.perYear),
            dollarsSaved: Math.round(this.totalDollarsSaved),
            baselinePercentageReduction: (baselinePercentageReduction * 100).toFixed(0),
            leedPoints: getLeedPoints(baselinePercentageReduction)
          };
        },

        graphYAxis() {
          return this.totalWaterUsage.leedPerYear >
          this.totalWaterUsage.perYear
            ? this.totalWaterUsage.leedPerYear
            : this.totalWaterUsage.perYear;
        }
      },
      watch: {
        selectedProducts: {
          handler(value) {
            this.useLiters = this.selectedProducts.every(({unit}) => unit === 'liters');
            localStorage.setItem('waterSavingsProducts', JSON.stringify(value));
          },
          deep: true
        },

        activeStep(value) {
          localStorage.setItem('waterSavingsStep', value);
          this.scrollContainer.scrollTo(0, 0);
        }
      },
      mounted() {
        const savedProducts = localStorage.getItem('waterSavingsProducts');
        const activeStep = localStorage.getItem('waterSavingsStep');

        if (savedProducts) {
          this.selectedProducts = JSON.parse(savedProducts);
          this.selectedProducts.forEach(product => {
            if (product.type) {
              this.getProducts(product.type);
            }

            if (product.name) {
              this.getWaterUsage(product);
            }
          });
        } else {
          this.addProduct();
        }

        if (activeStep) {
          this.activeStep = parseInt(activeStep);
        }

        document.addEventListener('click', this.handleDocumentClick);
      },
      beforeDestroy() {
        document.removeEventListener('click', this.handleDocumentClick);
      },
      methods: {
        getProductVariations(event, inputIndex) {
          const value = event.target.value;
          if (value.length < 2) return;

          const helper = this.$algolia.helper(
            this.$algolia.client,
            this.$algolia.indexPrefix + 'product_variations',
            {
              attributesToRetrieve: ['*'],
              hitsPerPage: 30,
              query: value,
              filters: `(search_api_language:${this.langcode}) AND (variation_sustainable:Yes)`
            }
          );

          helper.on('result', content => {
            this.variationOptions[inputIndex] = content.results.hits.filter(
              hit =>
                hit.variation_flow_rate_formatted ||
                hit.variation_flush_volume_formatted ||
                hit.combo_flushometer_flush_volume_formatted
            );
          });

          helper.search();
        },
        selectVariation(product, variation, inputIndex) {
          product.materialNumber = variation.variation_sku;

          const productType = this.productTypes.find(type => type.label === variation.variation_type);
          product.type = productType.id;
          product.dailyFlushes = this.getDefaultDailyUses(product.type);

          this.getProducts(product.type);
          product.name = variation.product_title;

          this.getWaterUsage(product);
          product.usage = variation[productType.usageField];

          this.variationOptions[inputIndex] = [];
        },
        handleProductTypeChange(product) {
          product.name = '';
          product.usage = '';
          product.dailyFlushes = this.getDefaultDailyUses(product.type);
          this.getProducts(product.type);
        },

        handleProductChange(product) {
          product.usage = '';
          this.getWaterUsage(product);
        },
        getProducts(typeId) {
          const productType = this.productTypes.find(type => type.id === typeId);
          const indexName = productType.index;

          if (this.productOptions[typeId]) return;

          const helper = this.$algolia.helper(
            this.$algolia.client,
            indexName,
            {
              attributesToRetrieve: ['*'],
              hitsPerPage: 1000,
              filters: productType.filters || ''
            }
          );

          helper.on('result', content => {
            this.$set(
              this.productOptions,
              typeId,
              content.results.hits
            );

            this.productTypes.forEach(() => {
              const sortedProducts = this.productOptions[typeId].sort((a, b) => {
                if (a.product_title < b.product_title) return -1;
                if (a.product_title > b.product_title) return 1;
                return 0;
              });

              this.$set(this.productOptions, typeId, sortedProducts);
            });
          });

          helper.search();
        },
        getWaterUsage(product) {
          if (product.type === 'hybrid-urinal' || product.type === 'waterfree-urinal') {
            this.$set(this.usageOptions, product.name, [0.0]);
            return;
          }

          const productType = this.productTypes.find(type => type.id === product.type);
          const indexName = productType.variationsIndex;

          const helper = this.$algolia.helper(
            this.$algolia.client,
            indexName,
            {
              attributesToRetrieve: ['*'],
              hitsPerPage: 1000,
              filters: `(product_title:'${product.name}')`
            }
          );

          helper.on('result', content => {
            const flushVolumes = content.results.hits
              .map(hit => hit[productType.usageField])
              .filter((value, index, self) => self.indexOf(value) === index)
              .filter(val => val !== null && val !== undefined);

            this.$set(this.usageOptions, product.name, flushVolumes);
          });

          helper.search();
        },
        getProductByType(type) {
          return this.productTypes.find(({id}) => id === type);
        },
        getDefaultDailyUses(type) {
          return type === 'showerhead' ? 0 : 3;
        },
        syncProjectDetails(event, key) {
          const value = event.target.value;
          this.selectedProducts = this.selectedProducts.map(
            product => {
              product[key] = value;
              return product;
            }
          );
        },
        addProduct() {
          let daysOccupancy = 260;
          let numberOccupants = 50;

          if (this.selectedProducts.length) {
            daysOccupancy = this.selectedProducts[0].daysOccupancy;
            numberOccupants = this.selectedProducts[0].numberOccupants;
          }

          this.selectedProducts.push({
            type: '',
            name: '',
            unit: 'gallons',
            usage: '',
            numberProducts: 50,
            waterSewerCosts: 7,
            daysOccupancy,
            numberOccupants,
            dailyFlushes: 3
          });
        },
        removeProduct(index) {
          this.selectedProducts.splice(index, 1);
        },
        reset() {
          localStorage.removeItem('waterSavingsProducts');
          localStorage.removeItem('waterSavingsStep');
          location.reload();
        },
        handleDocumentClick(event) {
          if (
            this.variationOptions &&
            this.$refs.variationOptions &&
            this.variationOptions.length &&
            this.$refs.variationOptions.length &&
            !this.$refs.variationOptions[0].contains(event.target)
          ) {
            this.variationOptions = [];
          }
        },
        handleStepClick(n) {
          if (this.activeStep > n) {
            this.activeStep = n;
          }
        }
      },
      router
    });
  }
};

export default loadCalculator;
